import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import Highlight from '../../components/base/Highlight';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createFAQStructuredData, createBreadcrumbStructuredData, createHowToStructuredData } from '../../utils/structuredData';

export default function DepositWithdrawal() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sho-navi.com';

  // SEO設定
  usePageSEO({
    title: 'DMM FX入出金方法完全ガイド｜手数料無料で即時反映',
    description: 'DMM FXの入金・出金方法を徹底解説。クイック入金なら24時間365日手数料無料で即時反映。約340の金融機関に対応し、出金も翌営業日には完了。初心者でも安心の入出金手順を画像付きで詳しく説明します。',
    keywords: 'DMM FX, 入金方法, 出金方法, クイック入金, 手数料無料, 即時反映',
    ogImage: 'https://public.readdy.ai/ai/img_res/39a0e4488dc6d4aa76252f5c70f894c0.jpg',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FX入出金方法完全ガイド',
        description: 'DMM FXの入金・出金方法を徹底解説。手数料無料で即時反映のクイック入金、出金手順、対応金融機関などを詳しく説明します。',
        image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20deposit%20withdrawal&width=1200&height=630&seq=deposit-article&orientation=landscape',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        url: `${siteUrl}/deposit-withdrawal`
      }),
      // 入金手順のHowTo構造化データ
      createHowToStructuredData({
        name: 'DMM FXクイック入金の手順',
        description: 'DMM FXのクイック入金を使って、手数料無料で即時入金する方法を4ステップで解説します。',
        totalTime: 'PT5M',
        estimatedCost: {
          currency: 'JPY',
          value: '0'
        },
        steps: [
          {
            name: 'マイページにログイン',
            text: 'DMM FXのマイページにログインし、「入金」メニューを選択します。',
            image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20login%20page%20interface&width=800&height=600&seq=deposit-step1&orientation=landscape'
          },
          {
            name: '入金方法を選択',
            text: 'クイック入金または振込入金から選択。クイック入金なら即時反映されます。',
            image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20deposit%20method%20selection%20screen&width=800&height=600&seq=deposit-step2&orientation=landscape'
          },
          {
            name: '金融機関を選択',
            text: '対応している金融機関を選択し、入金額を入力します。',
            image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20bank%20selection%20screen&width=800&height=600&seq=deposit-step3&orientation=landscape'
          },
          {
            name: '入金完了',
            text: '金融機関のサイトで認証を完了すると、即座に口座に反映されます。',
            image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20deposit%20completion%20screen&width=800&height=600&seq=deposit-step4&orientation=landscape'
          }
        ]
      }),
      // 出金手順のHowTo構造化データ
      createHowToStructuredData({
        name: 'DMM FX出金の手順',
        description: 'DMM FXから出金する方法を4ステップで解説します。出金手数料は無料で、最短翌営業日に振込されます。',
        totalTime: 'PT3M',
        estimatedCost: {
          currency: 'JPY',
          value: '0'
        },
        steps: [
          {
            name: '出金申請',
            text: 'マイページから「出金」メニューを選択し、出金額を入力します。',
            image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20withdrawal%20request%20screen&width=800&height=600&seq=withdrawal-step1&orientation=landscape'
          },
          {
            name: '出金内容の確認',
            text: '出金額と振込先口座を確認し、申請を確定します。',
            image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20withdrawal%20confirmation%20screen&width=800&height=600&seq=withdrawal-step2&orientation=landscape'
          },
          {
            name: '処理完了',
            text: '平日15時までの申請は翌営業日、それ以降は翌々営業日に振込されます。',
            image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20withdrawal%20processing%20screen&width=800&height=600&seq=withdrawal-step3&orientation=landscape'
          },
          {
            name: '着金確認',
            text: '指定した銀行口座に出金額が振り込まれます。',
            image: 'https://readdy.ai/api/search-image?query=bank%20account%20deposit%20confirmation&width=800&height=600&seq=withdrawal-step4&orientation=landscape'
          }
        ]
      }),
      createFAQStructuredData([
        {
          question: 'クイック入金の手数料は本当に無料ですか?',
          answer: 'はい、クイック入金は完全無料です。何度入金しても手数料は一切かかりません。ただし、振込入金の場合は各金融機関の振込手数料がお客様負担となります。'
        },
        {
          question: '入金が反映されないのですが?',
          answer: 'クイック入金は通常即時反映されますが、システムメンテナンス中や金融機関側の問題で遅延する場合があります。30分以上反映されない場合はサポートにお問い合わせください。'
        },
        {
          question: '出金手数料は本当に無料ですか?',
          answer: 'はい、出金手数料は完全無料です。何度出金しても手数料はかかりません。また、出金回数に制限もありませんので、必要な時にいつでも出金できます。'
        },
        {
          question: '出金はいつでもできますか?',
          answer: '出金申請は24時間365日可能ですが、実際の振込は営業日のみとなります。平日15時までの申請は翌営業日、それ以降は翌々営業日に振込されます。'
        },
        {
          question: 'ポジション保有中でも出金できますか?',
          answer: 'はい、余剰証拠金の範囲内であれば出金可能です。ただし、証拠金維持率が100%を下回る場合は出金できません。'
        }
      ], `${siteUrl}/deposit-withdrawal`),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: '入出金方法', url: '/deposit-withdrawal' }
      ], siteUrl)
    ]
  });

  const quickDepositBanks = [
    '三菱UFJ銀行',
    'みずほ銀行',
    '三井住友銀行',
    'りそな銀行',
    '埼玉りそな銀行',
    'ゆうちょ銀行',
    '楽天銀行',
    'ジャパンネット銀行',
    'セブン銀行',
    'ソニー銀行',
    '住信SBIネット銀行',
    'イオン銀行',
  ];

  const depositSteps = [
    {
      number: '01',
      title: 'マイページにログイン',
      description: 'DMM FXのマイページにログインし、「入金」メニューを選択します。',
      icon: 'ri-login-box-line',
    },
    {
      number: '02',
      title: '入金方法を選択',
      description: 'クイック入金または振込入金から選択。クイック入金なら即時反映されます。',
      icon: 'ri-bank-card-line',
    },
    {
      number: '03',
      title: '金融機関を選択',
      description: '対応している金融機関を選択し、入金額を入力します。',
      icon: 'ri-building-line',
    },
    {
      number: '04',
      title: '入金完了',
      description: '金融機関のサイトで認証を完了すると、即座に口座に反映されます。',
      icon: 'ri-checkbox-circle-line',
    },
  ];

  const withdrawalSteps = [
    {
      number: '01',
      title: '出金申請',
      description: 'マイページから「出金」メニューを選択し、出金額を入力します。',
      icon: 'ri-file-list-line',
    },
    {
      number: '02',
      title: '出金内容の確認',
      description: '出金額と振込先口座を確認し、申請を確定します。',
      icon: 'ri-eye-line',
    },
    {
      number: '03',
      title: '処理完了',
      description: '平日15時までの申請は翌営業日、それ以降は翌々営業日に振込されます。',
      icon: 'ri-time-line',
    },
    {
      number: '04',
      title: '着金確認',
      description: '指定した銀行口座に出金額が振り込まれます。',
      icon: 'ri-money-dollar-circle-line',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'DMM FX取引ガイド', url: '/fx-trade' },
            { name: '入出金方法', url: '/deposit-withdrawal' }
          ]} />
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-6 py-3 rounded-full font-bold text-lg mb-6 shadow-lg">
              <i className="ri-vip-crown-fill text-2xl"></i>
              <span>入金・出金手数料 <Highlight variant="bold" color="red">完全無料</Highlight></span>
              <i className="ri-vip-crown-fill text-2xl"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              DMM FXの入金方法・<br />
              出金方法
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              クイック入金なら24時間365日、<Highlight variant="badge" color="red">手数料無料</Highlight>で即時反映。出金も<Highlight variant="badge" color="red">手数料無料</Highlight>で翌営業日には完了。DMM FXの入出金方法を詳しく解説します。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                <Highlight variant="bold" color="red">手数料0円</Highlight>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <i className="ri-download-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">入金</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-teal-600"></i>
                  <span className="text-sm">24時間365日対応</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-teal-600"></i>
                  <span className="text-sm font-bold text-teal-600"><Highlight variant="bold" color="red">手数料無料</Highlight>(クイック入金)</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-teal-600"></i>
                  <span className="text-sm"><Highlight variant="bold" color="red">即時</Highlight>反映</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-teal-600"></i>
                  <span className="text-sm">約340の金融機関に対応</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                <Highlight variant="bold" color="red">手数料0円</Highlight>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <i className="ri-upload-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">出金</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span className="text-sm font-bold text-emerald-600"><Highlight variant="bold" color="red">出金手数料無料</Highlight></span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span className="text-sm">最短翌営業日振込</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span className="text-sm">2,000円から出金可能</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span className="text-sm">回数制限なし</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Free Fee Highlight Section */}
      <section className="py-16 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-4 border-amber-400">
            <div className="text-center mb-8">
              <div className="inline-block bg-gradient-to-r from-amber-400 to-orange-500 text-white px-8 py-3 rounded-full font-bold text-2xl mb-4 shadow-lg">
                <i className="ri-money-dollar-circle-line mr-2"></i>
                <Highlight variant="bold" color="red">手数料完全無料</Highlight>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                入金も出金も<span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600"><Highlight variant="bold" color="red">0円</Highlight></span>
              </h2>
              <p className="text-lg text-gray-600">
                他社では数百円かかる入出金手数料が、DMM FXなら<Highlight variant="marker" color="red">完全無料</Highlight>！
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                    <i className="ri-download-cloud-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">クイック入金</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">入金手数料</span>
                    <span className="text-2xl font-bold text-teal-600"><Highlight variant="bold" color="red">0円</Highlight></span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">対応金融機関</span>
                    <span className="font-bold text-gray-900">約340行</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">反映時間</span>
                    <span className="font-bold text-gray-900"><Highlight variant="bold" color="red">即時</Highlight></span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center">
                    <i className="ri-upload-cloud-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">出金</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">出金手数料</span>
                    <span className="text-2xl font-bold text-emerald-600"><Highlight variant="bold" color="red">0円</Highlight></span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">出金回数制限</span>
                    <span className="font-bold text-gray-900">なし</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">最低出金額</span>
                    <span className="font-bold text-gray-900">2,000円</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-6 text-white text-center">
              <p className="text-lg font-bold mb-2">
                <i className="ri-calculator-line mr-2"></i>
                年間コスト削減例
              </p>
              <p className="text-sm mb-3">月2回入金・月1回出金の場合（他社比較）</p>
              <div className="text-4xl font-bold mb-2">
                年間 <Highlight variant="bold" color="red">約10,800円 お得！</Highlight>
              </div>
              <p className="text-sm text-teal-100">
                ※他社平均：入金300円/回、出金600円/回で計算
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Deposit Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block bg-teal-100 text-teal-700 px-6 py-2 rounded-full font-bold mb-4">
              手数料無料
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              クイック入金の手順
            </h2>
            <p className="text-gray-600">約340の金融機関に対応、24時間365日いつでも即時反映</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {depositSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${step.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="text-3xl font-bold text-teal-600 mb-2">{step.number}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < depositSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-2xl text-teal-400"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
              対応金融機関(一部)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {quickDepositBanks.map((bank, index) => (
                <div key={index} className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <p className="text-sm font-medium text-gray-700">{bank}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-600 text-center mt-6">
              その他、約340の金融機関に対応しています
            </p>
          </div>

          {/* 利用者の声へのリンク */}
          <div className="mt-12 bg-white rounded-xl p-6 border-l-4 border-teal-500 shadow-md">
            <p className="text-gray-700 leading-relaxed">
              実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
              → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Withdrawal Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block bg-emerald-100 text-emerald-700 px-6 py-2 rounded-full font-bold mb-4">
              手数料無料・回数制限なし
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">出金の手順</h2>
            <p className="text-gray-600">何度出金しても手数料0円、最短翌営業日に着金</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {withdrawalSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${step.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="text-3xl font-bold text-emerald-600 mb-2">{step.number}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < withdrawalSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-2xl text-emerald-400"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-6">出金の注意事項</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-time-line text-xl text-emerald-600 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">出金タイミング</h4>
                  <p className="text-sm text-gray-600">
                    平日15時までの申請は翌営業日、15時以降の申請は翌々営業日に振込されます。
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-money-dollar-circle-line text-xl text-emerald-600 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">最低出金額</h4>
                  <p className="text-sm text-gray-600">
                    2,000円から出金可能です。<strong className="text-emerald-600">出金手数料は完全無料</strong>で、回数制限もありません。
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-shield-check-line text-xl text-emerald-600 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">出金可能額</h4>
                  <p className="text-sm text-gray-600">
                    証拠金維持率が100%を下回る場合は出金できません。ポジション保有中は余剰証拠金の範囲内で出金可能です。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">入金方法の比較</h2>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">項目</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">クイック入金</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">振込入金</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">手数料</td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-block bg-gradient-to-r from-amber-400 to-orange-500 text-white px-4 py-2 rounded-full text-base font-bold shadow-md">
                        <Highlight variant="bold" color="red">完全無料</Highlight>
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">お客様負担</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">反映時間</td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-block bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-semibold">
                        <Highlight variant="bold" color="red">即時</Highlight>
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">1時間程度</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">対応時間</td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-block bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-semibold">
                        24時間365日
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">銀行営業時間</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">最低入金額</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">5,000円</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">制限なし</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">よくある質問</h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-8 shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal-600"></i>
                クイック入金の手数料は本当に無料ですか?
              </h3>
              <p className="text-gray-600 leading-relaxed pl-8">
                はい、<Highlight variant="marker" color="red">クイック入金は完全無料</Highlight>です。何度入金しても手数料は一切かかりません。ただし、振込入金の場合は各金融機関の振込手数料がお客様負担となります。
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal-600"></i>
                入金が反映されないのですが?
              </h3>
              <p className="text-gray-600 leading-relaxed pl-8">
                クイック入金は通常<Highlight variant="bold" color="red">即時反映</Highlight>されますが、システムメンテナンス中や金融機関側の問題で遅延する場合があります。30分以上反映されない場合はサポートにお問い合わせください。
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal-600"></i>
                出金手数料は本当に無料ですか?
              </h3>
              <p className="text-gray-600 leading-relaxed pl-8">
                はい、<Highlight variant="marker" color="red">出金手数料は完全無料</Highlight>です。何度出金しても手数料はかかりません。また、出金回数に制限もありませんので、必要な時にいつでも出金できます。
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal-600"></i>
                出金はいつでもできますか?
              </h3>
              <p className="text-gray-600 leading-relaxed pl-8">
                出金申請は24時間365日可能ですが、実際の振込は営業日のみとなります。平日15時までの申請は翌営業日、それ以降は翌々営業日に振込されます。
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal-600"></i>
                ポジション保有中でも出金できますか?
              </h3>
              <p className="text-gray-600 leading-relaxed pl-8">
                はい、余剰証拠金の範囲内であれば出金可能です。ただし、証拠金維持率が100%を下回る場合は出金できません。
              </p>
            </div>
          </div>

          {/* 口座開設手順へのリンク */}
          <div className="mt-12 bg-white rounded-xl p-6 border-l-4 border-emerald-500 shadow-md">
            <p className="text-gray-700 leading-relaxed">
              口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
              → <Link to="/account-opening" className="text-emerald-600 font-semibold hover:text-emerald-700 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Related Articles Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            入出金についてさらに詳しく知りたい方へ
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Link 
              to="/account-opening" 
              className="group bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6 border-2 border-teal-100 hover:border-teal-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-file-list-3-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                    口座開設の手順
                  </h3>
                  <p className="text-sm text-gray-600">
                    画像付きで口座開設の流れを詳しく解説
                  </p>
                </div>
              </div>
            </Link>

            <Link 
              to="/app-guide" 
              className="group bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border-2 border-emerald-100 hover:border-emerald-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-smartphone-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                    アプリで入出金
                  </h3>
                  <p className="text-sm text-gray-600">
                    スマホアプリから簡単に入出金する方法
                  </p>
                </div>
              </div>
            </Link>

            <Link 
              to="/campaign" 
              className="group bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-amber-100 hover:border-amber-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-gift-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                    キャンペーン情報
                  </h3>
                  <p className="text-sm text-gray-600">
                    入金でもらえるキャッシュバックキャンペーン
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-teal-500 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-block bg-white/20 backdrop-blur-sm text-white px-6 py-2 rounded-full font-bold mb-6">
            <i className="ri-gift-line mr-2"></i>
            入金・出金手数料 <Highlight variant="bold" color="red">完全無料</Highlight>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            手数料無料で<br />
            スムーズな入出金
          </h2>
          <p className="text-lg text-teal-50 mb-8 max-w-2xl mx-auto">
            DMM FXならクイック入金で<Highlight variant="bold" color="red">即時反映</Highlight>、出金も翌営業日には完了。<strong className="text-white">入金・出金ともに<Highlight variant="bold" color="red">手数料0円</Highlight></strong>で、快適な取引環境を体験しましょう。
          </p>
          <a
            href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82"
            rel="nofollow"
            className="inline-block bg-white text-teal-600 px-10 py-4 rounded-lg text-lg font-bold hover:bg-gray-50 transition-all shadow-lg whitespace-nowrap cursor-pointer"
          >
            【PR】無料で口座開設する
          </a>
          {/* 1×1 tracking pixel */}
          <img
            border="0"
            width="1"
            height="1"
            src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82"
            alt=""
            style={{ display: 'none' }}
          />
          <p className="text-sm text-teal-50 mt-6">
            最短即日で取引開始可能 | 口座開設・維持費無料
          </p>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}
